import { MNElementContent } from '../elements/elementModelTypes';
import { TiptapContent, TiptapContentNode, TiptapNodeType } from '../tiptap/tiptapTypes';
import { getTaskListLabelIds } from './taskListLabelUtils';
import { TaskListContentVersion } from './taskListContentVersionUtils';

/**
 * Creates the minimal editable Tiptap document for a blank single-editor task list.
 */
export const createInitialSingleEditorTaskListDocument = (): TiptapContent => ({
    type: TiptapNodeType.doc,
    content: [
        {
            type: TiptapNodeType.taskList,
            content: [
                {
                    type: TiptapNodeType.taskItem,
                    attrs: { checked: false },
                    content: [
                        {
                            type: TiptapNodeType.paragraph,
                        },
                    ],
                },
            ],
        },
    ],
});

/**
 * Creates the persisted content payload for a new single-editor task list element.
 */
export const createInitialSingleEditorTaskListContent = (): MNElementContent => ({
    version: TaskListContentVersion.SingleEditor,
    textContent: createInitialSingleEditorTaskListDocument(),
});

/**
 * Creates a single-editor task list document containing the given task item nodes, with an
 * optional title node ahead of them. Omitting the title renders the list without one.
 *
 * The title leads the document because `TaskListDocument`'s content is `taskListTitle? taskList`.
 */
export const createSingleEditorTaskListDocumentWithItems = (
    taskItems: TiptapContentNode[],
    title?: TiptapContentNode,
): TiptapContent => ({
    type: TiptapNodeType.doc,
    content: [
        ...(title ? [title] : []),
        {
            type: TiptapNodeType.taskList,
            content: taskItems,
        },
    ],
});

/**
 * Creates the content payload for a task list element holding an existing document.
 *
 * The `labels` aggregate is derived here rather than left to the caller:
 * - A task item's label chip renders from the labels registered on the owning element, so a document
 *   whose labels never reach the element renders none of them.
 */
export const createSingleEditorTaskListContent = (textContent: TiptapContent): MNElementContent => {
    const content: MNElementContent = {
        version: TaskListContentVersion.SingleEditor,
        textContent,
    };

    const labels = getTaskListLabelIds(textContent);

    // Only add labels if they exist - to prevent unnecessarily adding the element to the "labelsBySection" index
    if (labels.length) {
        content.labels = labels;
    }

    return content;
};

/**
 * Creates the content payload for a task list element seeded with existing task item nodes
 * (e.g. ones dragged out of another single-editor task list, or pasted onto a board).
 */
export const createSingleEditorTaskListContentWithItems = (
    taskItems: TiptapContentNode[],
    title?: TiptapContentNode,
): MNElementContent => createSingleEditorTaskListContent(createSingleEditorTaskListDocumentWithItems(taskItems, title));
